/**
 * ---------------------------------------------------------------------------------------------------------------------
 * COMPRADO CONJUNTAMENTE IO
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * El elemento nace vacío y se rellena pidiendo a `/recommendations/products` que vuelva a renderizar
 * la sección: es la única forma de que Liquid tenga el objeto `recommendations` de Search & Discovery.
 *
 * A diferencia de `<product-recommendations>` del tema, este busca en la respuesta su propio id, para
 * poder convivir con el bloque nativo de complementarios sin que se copien el contenido el uno al otro.
 */

class BoughtTogetherIo extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    if (this.#loaded) return;
    this.#loaded = true;

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-bt-select]')) this.#refresh();
    });

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button) this.#submit(button);
    });

    // La talla elegida arriba manda: si no, es fácil añadir una distinta de la que se está mirando.
    document.addEventListener('variant:change', (event) => {
      // OJO: `detail.formId` no sirve para esto. El tema lo llena con `form.id`, y como el
      // formulario lleva un <input name="id"> (la variante), el acceso por nombre gana a la
      // propiedad del elemento: lo que llega ahí es ese input, no el id del formulario.
      if (event.target?.getAttribute?.('id') !== this.getAttribute('form')) return;
      this.#syncCurrentProduct(event.detail?.variant?.id);
    });

    // En el personalizador el bloque viene ya con un hueco pintado: hay que sumarle el total.
    if (this.querySelector('[data-bt-item]')) this.#refresh();

    this.#load();
  }

  get #items() {
    return Array.from(this.querySelectorAll('[data-bt-item]'));
  }

  async #load() {
    let content = await this.#fetchContent('complementary');

    if (!content && this.hasAttribute('fallback-related')) {
      content = await this.#fetchContent('related');
    }

    if (!content) {
      // En el personalizador se queda el hueco: si se quita, el bloque no se puede ni seleccionar.
      if (!this.hasAttribute('design-mode')) this.remove();
      return;
    }

    this.replaceChildren(...content);
    this.hidden = false;
    this.#refresh();
  }

  /** Devuelve los hijos del elemento gemelo en la respuesta, o null si no hay nada que enseñar. */
  async #fetchContent(intent) {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const sectionId = section.id.replace('shopify-section-', '');
    const url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute(
      'product'
    )}&limit=4&section_id=${encodeURIComponent(sectionId)}&intent=${intent}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
      const twin = document_.getElementById(this.id);

      if (!twin || twin.childElementCount === 0) return null;

      return window.document.importNode(twin, true).childNodes;
    } catch {
      return null;
    }
  }

  #syncCurrentProduct(variantId) {
    const select = this.#items[0]?.querySelector('[data-bt-select]');
    if (!select || !variantId) return;
    if (!select.querySelector(`option[value="${variantId}"]`)) return;

    select.value = String(variantId);
    this.#refresh();
  }

  /** Reparte lo que hay elegido: precio de cada mitad, total, y si el paquete se puede comprar. */
  #refresh() {
    let total = 0;
    let buyable = this.#items.length > 0;

    for (const item of this.#items) {
      const select = item.querySelector('[data-bt-select]');

      if (select) {
        const option = select.selectedOptions[0];

        item.dataset.variantId = select.value;
        item.dataset.price = option?.dataset.price ?? '0';

        const price = item.querySelector('[data-bt-price]');
        if (price && option?.dataset.priceFormatted) price.textContent = option.dataset.priceFormatted;

        // Sincronizar con la talla de arriba puede caer en una agotada, que sí se puede fijar por JS.
        if (option?.disabled) buyable = false;
      }

      total += Number(item.dataset.price ?? 0);
    }

    const totalElement = this.querySelector('[data-bt-total]');
    if (totalElement) totalElement.textContent = this.#formatMoney(total);

    const button = this.querySelector('button');
    if (button) button.disabled = !buyable;
  }

  async #submit(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const items = this.#items.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
    if (items.length === 0) return;

    button.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    // El cajón del carrito se apunta aquí para que la respuesta traiga ya su HTML nuevo.
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // Sin esto Shopify no comprueba el stock
      },
      body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname })
    });

    const json = await response.json();

    button.removeAttribute('aria-busy');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (!response.ok) {
      this.#showError(json.description || json.message);
      return;
    }

    if (
      window.themeVariables?.settings.cartType === 'page' ||
      window.themeVariables?.settings.pageType === 'cart'
    ) {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cart.sections = json.sections;

    // Mismos eventos que dispara `<product-form>`: así el cajón se refresca y se abre igual que siempre.
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
      })
    );
  }

  #showError(message) {
    const container = this.querySelector('.bought-together');
    if (!container || !message) return;

    const banner = document
      .createRange()
      .createContextualFragment(`<div class="banner banner--error" role="alert">${message}</div>`)
      .firstElementChild;

    container.prepend(banner);
    setTimeout(() => banner.remove(), 4000);
  }

  /**
   * El formato de la tienda llega en `money-format` (por ejemplo `{{amount_with_comma_separator}}€`).
   * Los precios de cada producto los formatea Liquid; aquí solo hay que formatear la suma.
   */
  #formatMoney(cents) {
    const format = this.getAttribute('money-format') || '{{amount}}';

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, name) => {
      const decimals = name.includes('no_decimals') ? 0 : 2;

      let thousands = ',';
      let decimal = '.';

      if (name.includes('comma_separator')) {
        thousands = '.';
        decimal = ',';
      } else if (name.includes('apostrophe_separator')) {
        thousands = "'";
      } else if (name.includes('space_separator')) {
        thousands = ' ';
        if (!name.includes('period_and_space')) decimal = ',';
      }

      const [integer, fraction] = (cents / 100).toFixed(decimals).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      return decimals ? `${grouped}${decimal}${fraction}` : grouped;
    });
  }
}

if (!window.customElements.get('bought-together-io')) {
  window.customElements.define('bought-together-io', BoughtTogetherIo);
}
